import { Box, Grid, Typography } from "@mui/material";
import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import {
    selectAddOrderEnabled,
    selectBasketId,
    selectBatchId,
    selectIceberg,
    selectStrategyBuilderTab,
    selectTradeOrders,
} from "../../selectors/strategyBuilder";
import { selectOrdersHistory } from "../../selectors/orders";
import { getIcebergLegOrdersForSecurity, getIcebergLegUiFromLegs } from "../../util/icebergOrders";
import { ExtraSmallButton, ExtraSmallButtonHoverable, NonInteractiveTooltip } from "../custom";
import Chart from "../../assets/icons/Chart";
import { generatePath, matchPath, useLocation } from "react-router-dom";
import ROUTES from "../../assets/routes";
import { StrategyBuilderTab } from "../strategyBuilder/header";
import { useDispatch } from "react-redux";
import { setOptionChainDrawerOpen } from "../../reducer/options.reducer";
import AddIcon from "../../assets/icons/AddIcon";
import BinIcon from "../../assets/icons/BinIcon";
import ResetIcon from "../../assets/icons/ResetIcon";
import { updateTradeOrders } from "../../reducer/strategyBuilder.reducer";
import { getStrategyExpressionParams } from "../../util/tradepanel";
import { Security } from "../../proto/generated/candle-data-depot-common";
import { getSecurityKey } from "../../util/security";
import { getOrderBook, getSecuritySnapshot } from "../../selectors/security";
import { limitPriceFromOrderBookOrLtp } from "../../util/orderBookPrice";
import { selectPositions } from "../../selectors/positions";
import { TransactionType } from "../../proto/generated/order";
import { getStrategyName } from "../../services/apis/misc";
import useSelectPrimaryOrUnderlyingSecurity from "../../hooks/useSelectPrimaryOrUnderlyingSecurity";
import { Group, setFeatureRestrictionDialog } from "../../reducer/user.reducer";
import { FeatureCode } from "../../util/RestrictionFeatureCode";

interface Props {
    strategyChartAccess: boolean;
}

const actionButtonSx = {
    width: 30,
    height: 30,
    minWidth: 30,
    p: 0,
    "& .MuiSvgIcon-root": { fontSize: 18 },
} as const;

const TradePanelActionBar: React.FC<Props> = ({ strategyChartAccess }) => {
    const location = useLocation();
    const dispatch = useDispatch();
    const { primaryOrUnderlying: mainSecurity } = useSelectPrimaryOrUnderlyingSecurity()
    const addOrderEnabled = useSelector(selectAddOrderEnabled);
    const tradeOrders = useSelector(selectTradeOrders);
    const basketId = useSelector(selectBasketId);
    const batchId = useSelector(selectBatchId);
    const iceberg = useSelector(selectIceberg);
    const placedOrders = useSelector(selectOrdersHistory);
    const positions = useSelector(selectPositions);
    const selectedTab = useSelector(selectStrategyBuilderTab);
    const [strategyName, setStrategyName] = useState<string>("")

    const optionPositionsRequest = useMemo(() => {
        return {
            optionPosition: tradeOrders.map(t => ({
                optionType: t.security.optionType,
                transactionType: t.transactionType,
                strikePrice: t.security.strikePrice,
                expiry: t.security.expiry.expiry,
                lots: t.quantity
            }))
        }
    }, [tradeOrders])

    useEffect(() => {
        if (!optionPositionsRequest.optionPosition.length) {
            setStrategyName("")
            return
        }
        const fetchStrategy = async () => {
            try {
                const res = await getStrategyName({
                    body: optionPositionsRequest
                })
                if(res.strategyName == 'Custom'){
                    setStrategyName(`${mainSecurity?.symbol ?? ''} Basket`)
                    return
                }
                setStrategyName(`${mainSecurity?.symbol ?? ''} - ${res.strategyName}`)
            } catch (e) {
                console.error("Strategy API failed", e)
                setStrategyName("")
            }
        }
        fetchStrategy()
    }, [optionPositionsRequest.optionPosition])

    const basketPositions = useMemo(() => {
        return positions.filter(p => p.basketId === basketId);
    }, [positions, basketId]);

    const showAddLeg = useMemo(() => {
        const optionChainPath = matchPath(ROUTES.optionChain, location.pathname)
        const strategyPath = matchPath(ROUTES.strategyBuilder, location.pathname)
        const isOptionChainOpenInStrategy = selectedTab === StrategyBuilderTab.OptionChain
        return addOrderEnabled && !optionChainPath && !(strategyPath && isOptionChainOpenInStrategy)
    }, [location, selectedTab, addOrderEnabled])

    const handleClearTrade = useCallback(() => {
        dispatch(updateTradeOrders([]))
    }, [])

    const strategyChartPath = useMemo(() => {
        const trades = basketPositions.reduce((acc, p) => {
            const securityKey = getSecurityKey(p.security.exchange, p.security.code);
            return {
                ...acc,
                [securityKey]: {
                    quantity: (acc[securityKey]?.quantity || 0) + p.closePosition,
                    security: p.security
                }
            }
        }, {} as Record<string, { quantity: number, security: Security }>)

        tradeOrders.forEach((t) => {
            const securityKey = getSecurityKey(t.security.exchange, t.security.code);
            if (!trades[securityKey]) {
                trades[securityKey] = {
                    quantity: 0,
                    security: t.security
                }
            }
            if (t.transactionType === TransactionType.BUY) {
                trades[securityKey].quantity += t.quantity
            } else {
                trades[securityKey].quantity -= t.quantity
            }
        })
        const { param } = getStrategyExpressionParams(Object.values(trades).filter(t => t.quantity !== 0));
        return generatePath(ROUTES.spread, { spread: param })
    }, [tradeOrders, basketPositions])

    const ordersHeadingLeg = useMemo(() => {
        if (!iceberg.enabled || !batchId || !basketId || !tradeOrders.length) return null;
        const ref = tradeOrders.find((t) => t.selected) ?? tradeOrders[0];
        const legs = getIcebergLegOrdersForSecurity(placedOrders || [], batchId, basketId, ref.security);
        const ui = getIcebergLegUiFromLegs(legs, iceberg.numberOfLegs);
        return ui ?? null;
    }, [iceberg.enabled, iceberg.numberOfLegs, batchId, basketId, tradeOrders, placedOrders]);

    const title = useMemo(() => {
        const legEl = ordersHeadingLeg ? (
            <Typography
                component="span"
                color="text.secondary"
                fontSize="11px"
                fontWeight={500}
                sx={{ whiteSpace: "nowrap", flexShrink: 0 }}
            >
                Leg {ordersHeadingLeg.k}/{ordersHeadingLeg.n}
            </Typography>
        ) : null;

        if (basketPositions?.length) {
            if (ordersHeadingLeg) {
                return (
                    <Box component="span" sx={{ display: "inline-flex", alignItems: "baseline", gap: 0.75, minWidth: 0 }}>
                        <Box component="span" sx={{ whiteSpace: "nowrap" }}>
                            Orders
                        </Box>
                        {legEl}
                    </Box>
                );
            }
            return "Orders";
        }
        if (ordersHeadingLeg) {
            return (
                <Box component="span" sx={{ display: "inline-flex", alignItems: "baseline", gap: 0.75, minWidth: 0 }}>
                    <Box
                        component="span"
                        sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
                    >
                        {strategyName}
                    </Box>
                    {legEl}
                </Box>
            );
        }
        return strategyName;
    }, [strategyName, basketPositions, ordersHeadingLeg]);


    const handleResetPrices = useCallback(() => {
        dispatch(updateTradeOrders(tradeOrders.map((t) => {
            const security = t.security;
            const key = getSecurityKey(security.exchange, security.code);
            const orderBook = getOrderBook(key)
            const snapshot = getSecuritySnapshot(key)
            const price = limitPriceFromOrderBookOrLtp(orderBook, t.transactionType, snapshot?.lastTradedPrice)
            return {
                ...t,
                price: price || t.price
            }
        })))
    }, [tradeOrders])

    return (
        <Grid
            container
            item
            xs={12}
            alignItems="center"
            sx={{ mb: 1, flexWrap: "nowrap" }}
        >
            <Grid
                item sx={{flexGrow: 1,minWidth: 0}}
            >
                <Typography
                    fontSize="12px"
                    whiteSpace="nowrap"
                    overflow="hidden"
                    textOverflow="ellipsis"
                    fontWeight={500}
                    padding="5px 0 0 5px"
                >
                    {title}
                </Typography>
            </Grid>
            <Grid
                item
                 //xs={tradeOrders && (mode === "analyze" || tradeOrders.length > 1 || (basketId && basketId !== 'default')) ? 8 : 4}
                sx={{
                    display: "flex",
                    alignItems: "end",
                    gap: "4px",
                    flexShrink: 0, 
                }}
            >
                <NonInteractiveTooltip title="Charts" arrow placement="top">
                    <ExtraSmallButtonHoverable
                        variant="outlined"
                        color="inherit"
                        sx={{ width: 30, height: 30, minWidth: 30, maxWidth: 30, p: 0 }}
                        onClick={() => {
                            if (!strategyChartAccess) {
                                dispatch(setFeatureRestrictionDialog({
                                    open: true,
                                    feature: "Strategy chart",
                                    group: Group.STRATEGY_BUILDER,
                                    featureRestrictionCode: FeatureCode.STRATEGY_CHART_ACCESS
                                }));
                                return;
                            }
                            window.open(strategyChartPath, "_blank", "noopener,noreferrer");
                        }}
                    >
                        <Chart sx={{ fontSize: "28px" }} />
                    </ExtraSmallButtonHoverable>
                </NonInteractiveTooltip>

                {showAddLeg && (
                    <NonInteractiveTooltip title="Add Leg" arrow placement="top">
                        <ExtraSmallButton
                            data-event="Add Leg Clicked"
                            data-event-source="Trade"
                            variant="text"
                            color="inherit"
                            onClick={() => dispatch(setOptionChainDrawerOpen(true))}
                            size="small"
                            sx={actionButtonSx}
                        >
                            <AddIcon />
                        </ExtraSmallButton>
                    </NonInteractiveTooltip>
                )}

                <NonInteractiveTooltip title="Reset Prices" arrow placement="top">
                    <ExtraSmallButton
                        variant="text"
                        data-event="Reset Prices Clicked"
                        data-event-source="Trade"
                        color="inherit"
                        onClick={handleResetPrices}
                        size="small"
                        sx={actionButtonSx}
                    >
                        <ResetIcon />
                    </ExtraSmallButton>
                </NonInteractiveTooltip>

                <NonInteractiveTooltip title="Clear Trade" arrow placement="top">
                    <ExtraSmallButton
                        data-event="Clear Trade Clicked"
                        data-event-source="Trade"
                        variant="text"
                        color="inherit"
                        onClick={handleClearTrade}
                        size="small"
                        sx={actionButtonSx}
                    >
                        <BinIcon />
                    </ExtraSmallButton>
                </NonInteractiveTooltip>
            </Grid>
        </Grid>
    );
}
export default memo(TradePanelActionBar);